import type { ChartMetric } from '../generated/social-media/metapicSocialMediaAPIDocumentation.schemas'
import {
  type CampaignMetrics,
  CampaignOverviewFilterTargetEnum,
  type CampaignOverviewViewMode,
  type CampaignSourceMetrics,
} from '../types/campaign.types'

type FilterTargetConfig = {
  label: string
  // 'number' renders a plain formatted number, 'currency' prefixes the campaign currency.
  format: 'number' | 'currency'
  // Word shown next to the value in the chart tooltip (e.g. "1,000 views"). Currency targets
  // omit it since the symbol is already baked into the formatted value.
  unit?: string
  // Margin/earnings figures that should only be shown in the admin app, never to advertisers.
  adminOnly?: boolean
} &
  // Social targets are served by the social-media /performance endpoint (totals + chart).
  (| { source: 'social'; metric: ChartMetric }
    // Campaign-stats targets read a field off the campaign stats endpoint's metrics.
    | { source: 'campaignStats'; field: keyof Omit<CampaignMetrics, 'linksCreated'> }
  )

// Single source of truth per filter target, in display order. Buttons, chart series,
// formatting, tooltip units and social-vs-stats routing are all derived from this table.
export const FILTER_TARGET_CONFIG: Record<CampaignOverviewFilterTargetEnum, FilterTargetConfig> = {
  [CampaignOverviewFilterTargetEnum.ContentProduced]: {
    label: 'Content produced',
    format: 'number',
    source: 'social',
    metric: 'content_produced',
  },
  [CampaignOverviewFilterTargetEnum.EstViews]: {
    label: 'Est. views',
    format: 'number',
    unit: 'views',
    source: 'social',
    metric: 'views',
  },
  [CampaignOverviewFilterTargetEnum.EstUniqueViews]: {
    label: 'Est. unique views',
    format: 'number',
    unit: 'unique views',
    source: 'social',
    metric: 'unique_views',
  },
  [CampaignOverviewFilterTargetEnum.EstMediaValue]: {
    label: 'Est. media value',
    format: 'currency',
    source: 'social',
    metric: 'media_value',
  },
  [CampaignOverviewFilterTargetEnum.Clicks]: {
    label: 'Clicks',
    format: 'number',
    unit: 'clicks',
    source: 'campaignStats',
    field: 'clicks',
  },
  [CampaignOverviewFilterTargetEnum.Orders]: {
    label: 'Nr. of orders',
    format: 'number',
    unit: 'orders',
    source: 'campaignStats',
    field: 'orders',
  },
  [CampaignOverviewFilterTargetEnum.OrderValue]: {
    label: 'Order value',
    format: 'currency',
    source: 'campaignStats',
    field: 'orderValue',
  },
  [CampaignOverviewFilterTargetEnum.TotalCost]: {
    label: 'Total cost',
    format: 'currency',
    source: 'campaignStats',
    field: 'estimatedCost',
  },
  [CampaignOverviewFilterTargetEnum.TotalEarning]: {
    label: 'Est. total revenue',
    format: 'currency',
    source: 'campaignStats',
    field: 'estimatedEarnings',
    adminOnly: true,
  },
  [CampaignOverviewFilterTargetEnum.GPRatio]: {
    label: 'GP ratio',
    format: 'number',
    source: 'campaignStats',
    field: 'estimatedGpRatio',
    adminOnly: true,
  },
  [CampaignOverviewFilterTargetEnum.EstGP]: {
    label: 'Est. GP',
    format: 'currency',
    source: 'campaignStats',
    field: 'estimatedGp',
    adminOnly: true,
  },
}

export const FILTER_TARGETS = Object.keys(
  FILTER_TARGET_CONFIG,
) as CampaignOverviewFilterTargetEnum[]

/** Filter targets visible for a given app: advertisers never see admin-only margin/earnings figures. */
export const getFilterTargets = (
  viewMode: CampaignOverviewViewMode,
): CampaignOverviewFilterTargetEnum[] =>
  viewMode === 'admin'
    ? FILTER_TARGETS
    : FILTER_TARGETS.filter((target) => !FILTER_TARGET_CONFIG[target].adminOnly)

/** Which social-media metric (if any) a filter target is served by, used to drive the social chart/totals requests. */
export const getActiveSocialMetric = (
  target: CampaignOverviewFilterTargetEnum,
): ChartMetric | null => {
  const config = FILTER_TARGET_CONFIG[target]
  return config.source === 'social' ? config.metric : null
}

export const getFilterTargetMetricAmount = (
  target: CampaignOverviewFilterTargetEnum,
  socialMetricTotals: Partial<Record<ChartMetric, number>> | undefined,
  campaignStatsMetrics: CampaignMetrics | CampaignSourceMetrics | undefined,
): number => {
  const config = FILTER_TARGET_CONFIG[target]
  if (config.source === 'social') {
    return socialMetricTotals?.[config.metric] ?? 0
  }
  return campaignStatsMetrics?.[config.field] ?? 0
}
